<template>
  <div class="reportSettings">
    <Loading v-if="loading" />
    <div class="reportSettings__content">
      <!-- Automatic reports on / off -->
      <div class="reportSettings__section">
        <Checkbox
          name="automaticReports"
          :value="form.automaticReports"
          :title="$t('automaticReports')"
          :disabled="!canEdit"
          @change="toggleAutomaticReports"
        />
      </div>

      <!-- Report recipient e-mails (several can be added) -->
      <div class="reportSettings__section">
        <p class="reportSettings__label">{{ $t('reportEmails') }}</p>
        <div
          v-for="(email, idx) in form.emails"
          :key="idx"
          class="reportSettings__email"
        >
          <input
            v-model="form.emails[idx]"
            type="email"
            class="reportSettings__email-input"
            :placeholder="$t('email')"
            :disabled="!canEdit"
          />
          <img
            v-if="canEdit"
            class="reportSettings__email-remove"
            src="@/assets/icons/CloseInput.svg"
            alt="Remove"
            @click="removeEmail(idx)"
          />
        </div>
        <AddInfo :title="$t('reportEmails')" @click="addEmail" />
      </div>

      <!-- Frequency: weekly / monthly -->
      <div class="reportSettings__section">
        <p class="reportSettings__label">{{ $t('frequency') }}</p>
        <Dropdown
          class="reportSettings__dropdown"
          :selected-option="frequencyDropdown.selectedOption"
          :options="frequencyDropdown.options"
          :disabled="!canEdit"
          data-testid="selectReportFrequency"
          @select-option="selectFrequency"
        />
      </div>

      <!-- What to include in the report -->
      <div class="reportSettings__section">
        <p class="reportSettings__label">{{ $t('whatToInclude') }}</p>
        <div class="reportSettings__checkboxes">
          <Checkbox
            name="reportAllSensors"
            :value="form.include.allSensors"
            :title="$t('reportAllSensors')"
            :disabled="!canEdit"
            @change="toggleInclude('allSensors')"
          />
          <Checkbox
            name="reportRoomSensors"
            :value="form.include.roomTemperature"
            :title="$t('reportRoomSensors')"
            :disabled="!canEdit"
            @change="toggleInclude('roomTemperature')"
          />
          <Checkbox
            name="reportFridgeSensors"
            :value="form.include.fridges"
            :title="$t('reportFridgeSensors')"
            :disabled="!canEdit"
            @change="toggleInclude('fridges')"
          />
          <Checkbox
            name="reportAlarmsOnSeparatePage"
            :value="form.alarmsOnSeparatePage"
            :title="$t('reportAlarmsOnSeparatePage')"
            :disabled="!canEdit"
            @change="toggleAlarmsOnSeparatePage"
          />
        </div>
      </div>

      <!-- Sensors that get their own graph in the report -->
      <div v-if="sensors.length" class="reportSettings__section">
        <p class="reportSettings__label">{{ $t('reportIndividualSensors') }}</p>
        <div class="reportSettings__checkboxes">
          <Checkbox
            v-for="sensor in sensors"
            :key="sensor.id"
            :name="`reportSensor-${sensor.id}`"
            :value="form.include.individualSensorIds.includes(sensor.id)"
            :title="sensor.name"
            :disabled="!canEdit"
            @change="toggleIndividualSensor(sensor.id)"
          />
        </div>
      </div>

      <div class="reportSettings__actions">
        <Button
          class="reportSettings__save"
          variant="filled"
          :title="$t('save')"
          :disabled="!canSave"
          @click="save"
        />
        <p v-if="saved" class="reportSettings__saved">
          {{ $t('reportSettingsSaved') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';

import { Option } from '@/types';
import {
  ReportFrequency,
  ReportSettings as ReportSettingsData,
  ReportSettingsIncludeFlag,
  Sensor
} from '@/store/modules/pharmacy/types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Options({
  name: 'ReportSettings',
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      saved: false,
      form: {
        automaticReports: false,
        emails: [] as string[],
        frequency: 'weekly' as ReportFrequency,
        include: {
          allSensors: false,
          roomTemperature: false,
          fridges: false,
          individualSensorIds: [] as string[]
        },
        alarmsOnSeparatePage: false
      },
      frequencyDropdown: {
        selectedOption: null as Option | null,
        options: [] as Option[]
      }
    };
  },
  created() {
    this.frequencyDropdown.options = [
      { value: 'weekly', label: this.$t('weekly') },
      { value: 'monthly', label: this.$t('monthly') }
    ];

    if (this.user) {
      this.show();
    }
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    canEdit() {
      return !!this.user?.role?.permissions?.pharmacy?.update;
    },
    sensors(): Sensor[] {
      return this.pharmacy?.sensors || [];
    },
    emailsValid() {
      return this.form.emails.every(
        (email: string) => !email.trim() || EMAIL_REGEX.test(email.trim())
      );
    },
    canSave() {
      return this.canEdit && this.emailsValid;
    }
  },
  methods: {
    ...mapActions('pharmacy', ['getPharmacy', 'updatePharmacy']),
    async show() {
      if (!this.pharmacy) {
        this.loading = true;
        await this.getPharmacy(this.user.pharmacyId);
        this.loading = false;
      }

      this.initForm();
    },
    initForm() {
      const settings: ReportSettingsData | undefined =
        this.pharmacy?.reportSettings;

      if (settings) {
        this.form = {
          automaticReports: !!settings.automaticReports,
          emails: Array.isArray(settings.emails) ? [...settings.emails] : [],
          frequency: settings.frequency || 'weekly',
          include: {
            allSensors: !!settings.include?.allSensors,
            roomTemperature: !!settings.include?.roomTemperature,
            fridges: !!settings.include?.fridges,
            individualSensorIds: Array.isArray(
              settings.include?.individualSensorIds
            )
              ? [...settings.include.individualSensorIds]
              : []
          },
          alarmsOnSeparatePage: !!settings.alarmsOnSeparatePage
        };
      }

      this.frequencyDropdown.selectedOption =
        this.frequencyDropdown.options.find(
          (option: Option) => option.value === this.form.frequency
        ) || this.frequencyDropdown.options[0];
    },
    toggleAutomaticReports() {
      this.form.automaticReports = !this.form.automaticReports;
    },
    toggleInclude(key: ReportSettingsIncludeFlag) {
      this.form.include[key] = !this.form.include[key];
    },
    toggleIndividualSensor(sensorId: string) {
      const ids: string[] = this.form.include.individualSensorIds;
      const index = ids.indexOf(sensorId);

      if (index === -1) {
        ids.push(sensorId);
      } else {
        ids.splice(index, 1);
      }
    },
    toggleAlarmsOnSeparatePage() {
      this.form.alarmsOnSeparatePage = !this.form.alarmsOnSeparatePage;
    },
    addEmail() {
      if (!this.canEdit) return;
      this.form.emails.push('');
    },
    removeEmail(index: number) {
      this.form.emails.splice(index, 1);
    },
    selectFrequency(option: Option) {
      if (!option) return;
      this.frequencyDropdown.selectedOption = option;
      this.form.frequency = option.value as ReportFrequency;
    },
    async save() {
      if (!this.canSave) return;

      const emails = this.form.emails
        .map((email: string) => email.trim())
        .filter(Boolean);

      const reportSettings: ReportSettingsData = {
        automaticReports: this.form.automaticReports,
        emails,
        frequency: this.form.frequency,
        include: {
          ...this.form.include,
          individualSensorIds: [...this.form.include.individualSensorIds]
        },
        alarmsOnSeparatePage: this.form.alarmsOnSeparatePage
      };

      this.loading = true;

      await this.updatePharmacy({
        data: { reportSettings },
        pharmacyId: this.user.pharmacyId
      });

      // The update mutation clears the cached pharmacy, so re-fetch to keep the
      // rest of the app (and this form) in sync with the saved values.
      await this.getPharmacy(this.user.pharmacyId);

      this.form.emails = emails;
      this.loading = false;

      this.saved = true;
      setTimeout(() => {
        this.saved = false;
      }, 3000);
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.show();
      }
    }
  }
})
export default class ReportSettings extends Vue {}
</script>

<style lang="scss" scoped>
.reportSettings {
  padding: 0 24px 24px;
  .reportSettings__content {
    display: flex;
    flex-direction: column;
    max-width: 520px;
    padding-top: 24px;
    .reportSettings__section {
      margin-bottom: 32px;
      .reportSettings__label {
        font-family: 'Ubuntu-Medium';
        font-size: $sixteenSize;
        line-height: $fourteenSize;
        font-weight: 500;
        padding: 8px 0;
        margin-bottom: 16px;
      }
      .reportSettings__email {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        .reportSettings__email-input {
          width: 100%;
          height: 42px;
          padding: 0 12px;
          border: 1px solid $border;
          border-radius: 8px;
          font-size: $fourteenSize;
          color: $mid-grey;
          &:focus {
            outline: none;
            border-color: $main-green;
          }
          &:disabled {
            background-color: $light-grey;
          }
        }
        .reportSettings__email-remove {
          margin-left: 10px;
          width: 20px;
          height: 20px;
          cursor: pointer;
        }
      }
      .reportSettings__dropdown {
        height: 42px;
        width: 250px !important;
      }
      .reportSettings__checkboxes {
        display: flex;
        flex-direction: column;
        row-gap: 20px;
      }
    }
    .reportSettings__actions {
      display: flex;
      align-items: center;
      column-gap: 16px;
      .reportSettings__save {
        width: 160px;
      }
      .reportSettings__saved {
        color: $main-green;
        font-size: $fourteenSize;
      }
    }
  }
}
</style>
